/**
 * Workspace hub — Project Harmony
 *
 * Consolidates all technical configuration. Brand Brain · Billing ·
 * Settings · Integrations · API Keys · Business Info · Users ·
 * Permissions · Autopilot Settings · Trust Levels · Diagnostics ·
 * Developer Settings.
 */
import { Link } from "react-router-dom";
import {
  Brain, CreditCard, Rocket, Settings, ScanSearch,
  Sparkles, Wrench, GraduationCap, Network,
} from "lucide-react";

import { PageHeader } from "@/shared/ui/page-header";
import { useSurfacePolicy } from "@/entities/auth/useSurfacePolicy";

const SECTIONS = [
  { to: "/brand-brain",       label: "Brand Brain",         body: "Voice, values, positioning, offers.",         icon: Brain,       testId: "workspace-brand-brain" },
  { to: "/workspace/autopilot", label: "Autopilot",         body: "Trust level, health, department controls.",   icon: Sparkles,    testId: "workspace-autopilot" },
  { to: "/settings",          label: "Workspace Settings",  body: "Business info, operating mode, team, API keys.", icon: Settings, testId: "workspace-settings" },
  { to: "/workspace/business-discovery", label: "Business Discovery", body: "Scan your website, compare reports, and approve findings.", icon: ScanSearch, testId: "workspace-business-discovery" },
  { to: "/pricing",           label: "Plans & Pricing",     body: "Compare plans and upgrade your workspace.",   icon: Sparkles,    testId: "workspace-pricing" },
  { to: "/billing",           label: "Billing",             body: "Current plan, invoices, and usage.",          icon: CreditCard,  testId: "workspace-billing" },
  { to: "/workspace/growth-network", label: "Growth Network", body: "Referral identity, governed earnings, and network truth.", icon: Network, testId: "workspace-growth-network" },
  { to: "/academy",           label: "Academy",             body: "Learn how Orkexra works.",                icon: GraduationCap, testId: "workspace-academy" },
  { to: "/health",            label: "Diagnostics",         body: "Provider status, queue health, logs.",        icon: Wrench,      testId: "workspace-diagnostics" },
  { to: "/launch-readiness",  label: "Launch Readiness",    body: "Publishing readiness checklist.",             icon: Rocket,      testId: "workspace-launch" },
] as const;

export default function WorkspaceHubPage(): JSX.Element {
  const { canViewInternalDiagnostics } = useSurfacePolicy();
  const visibleSections = SECTIONS.filter(
    (section) =>
      canViewInternalDiagnostics ||
      (section.to !== "/health" && section.to !== "/launch-readiness"),
  );
  return (
    <div className="mx-auto max-w-[1120px] px-4 py-8 md:px-8 md:py-12" data-testid="workspace-hub">
      <PageHeader
        eyebrow="Workspace"
        title="Workspace"
        subtitle="Teach Orkexra about your business, manage your Marketing Department, and control the systems behind your workspace."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workspace-sections">
        {visibleSections.map((s) => (
          <Link
            key={s.testId}
            to={s.to}
            data-testid={s.testId}
            className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-5 transition hover:border-[var(--md-amber)]/50 hover:bg-[var(--md-ink-1)]"
          >
            <s.icon className="h-5 w-5 text-[var(--md-amber)]" aria-hidden />
            <p className="mt-3 text-sm font-semibold text-[var(--md-ivory-0)]">{s.label}</p>
            <p className="mt-1 text-xs text-[var(--md-fog)]">{s.body}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
